/**
 * CARD INSPECT. Hold the pointer on a card and see it properly.
 *
 * 🚨 **A CARD GAME WHOSE CARDS CANNOT BE READ.** Alex, 2026-08-15: *"for in game when you click on
 * your card once, or hover over it long enough for the tooltip to come up should we add a feature
 * that makes the card bigger so the player can really see it and read it and appreciate the artwork
 * and look of it? We also for sure need such feature on the build a deck page because the player can
 * not see the card in full and appreciate it and understand it."* And: *"a UI/UX and front end and
 * game design feature that our skills should have caught actually."*
 *
 * He is right, and the miss is structural rather than an oversight. A board creature renders as
 * `mini` - by deliberate design, because on the table you are TRACKING a card, not READING it - and
 * the deck builder uses the same mini for density. So the ONLY place the full card face exists is a
 * hand card, and the deck builder, where a player decides what to collect, never shows one at all.
 * **We built a card face and then never gave the player a way to look at it.**
 *
 * ⭐ **HOVER, NOT CLICK, AND THAT IS FORCED BY THE EXISTING CONTROLS.** Click is already taken
 * everywhere it matters: in the deck builder it adds a copy, in hand it plays, on the table it
 * selects an attacker. Rebinding click would trade one missing feature for three broken ones. A
 * hold reads as "I want to look at this" in every card game there is, and it composes with
 * everything instead of competing with it.
 *
 * ⛔ **IT NEVER TAKES A CLICK AND NEVER COVERS ITS OWN CARD.** `pointer-events: none`, and it is
 * placed on whichever side of the card has room. A preview that swallows the drag you were about to
 * start would be worse than no preview: the player would have lost a control to gain a picture.
 *
 * ⚠️ **ATTACHED INSIDE `cardEl`, SO EVERY CARD EVERYWHERE HAS IT.** Wiring it per screen is how the
 * deck builder ends up without it - which is precisely the screen that needed it most.
 */

import type { RuntimeObject } from "../match/state";

/**
 * ⚠️ A single shared node. One preview per pointer is the only correct number, and a per-card node
 * would leak one element per card ever hovered.
 */
let node: HTMLElement | null = null;
let timer: number | null = null;
let renderCard: ((c: RuntimeObject) => HTMLElement) | null = null;

/**
 * The card whose native `title` we are holding, and the text we took off it.
 *
 * 🚨 **THE OS TOOLTIP PAINTS OVER THE PREVIEW AND NO `z-index` CAN STOP IT.** Alex, 2026-08-15:
 * *"on the build deck page if you scroll over a card and it expands and the tooltips come up the
 * tooltips cover the expanded card."* Every card root carries `title=` (`match/card.ts:244`), so
 * hovering shows BOTH: our preview, drawn by the page, and the browser's own tooltip, drawn by the
 * OS in a layer the page cannot reach. Raising the preview's z-index does nothing, because they are
 * not in the same stacking context — or in the same window.
 *
 * ⭐ **SO THE PREVIEW TAKES THE TITLE AWAY WHILE IT IS UP, AND GIVES IT BACK ON HIDE.** The preview
 * is strictly more information than the tooltip (full art, full rules, at a readable size), so there
 * is nothing to lose while it is showing — and the tooltip must survive for keyboard and touch
 * users, who never trigger the preview at all. **Deleting the `title` outright would fix the overlap
 * by removing an accessibility affordance, which is not a fix.**
 */
let mutedTitleEl: HTMLElement | null = null;
let mutedTitle = "";

function muteNativeTooltip(el: HTMLElement): void {
  restoreNativeTooltip();
  if (!el.title) return;
  mutedTitleEl = el;
  mutedTitle = el.title;
  el.title = "";
}

function restoreNativeTooltip(): void {
  if (mutedTitleEl) mutedTitleEl.title = mutedTitle;
  mutedTitleEl = null;
  mutedTitle = "";
}

/**
 * Hand the zoom its renderer.
 *
 * ⚠️ **INJECTED RATHER THAN IMPORTED, TO BREAK A CYCLE.** `cardEl` attaches the zoom and the zoom
 * draws a card with `cardEl`; importing it directly is a circular import that Vite resolves to
 * `undefined` at runtime rather than failing at build - a blank preview with no error anywhere.
 */
export function setZoomRenderer(fn: (c: RuntimeObject) => HTMLElement): void {
  renderCard = fn;
}

function hide(): void {
  if (timer !== null) { window.clearTimeout(timer); timer = null; }
  node?.remove();
  node = null;
  restoreNativeTooltip();
}

function show(anchor: HTMLElement, c: RuntimeObject): void {
  if (!renderCard) return;
  hide();
  // Take the OS tooltip out of the way for exactly as long as the preview is up. See the note on
  // `mutedTitleEl` - this is not a z-index problem and cannot be solved as one.
  muteNativeTooltip(anchor);

  const wrap = document.createElement("div");
  wrap.className = "cardzoom";
  wrap.setAttribute("aria-hidden", "true");   // the card's own label already carries the name
  wrap.append(renderCard(c));

  // ⚠️ Mounted on the STAGE, not the body: the stage is scaled to fit the window, and a preview
  // outside it would be the only object on screen at a different scale to everything else.
  const stage = anchor.closest<HTMLElement>(".stage") ?? document.body;
  stage.append(wrap);

  // ── Place it beside the card, on whichever side has room ──────────────────────────────────────
  const a = anchor.getBoundingClientRect();
  const s = stage.getBoundingClientRect();
  const w = wrap.offsetWidth, h = wrap.offsetHeight;
  const scale = (s.width / (stage.offsetWidth || s.width)) || 1;

  // Convert to the stage's own coordinate space, which is what `left`/`top` are relative to.
  const ax = (a.left - s.left) / scale;
  const ay = (a.top - s.top) / scale;
  const aw = a.width / scale, ah = a.height / scale;
  const stageW = stage.offsetWidth, stageH = stage.offsetHeight;

  const GAP = 14;
  let x = ax + aw + GAP;
  if (x + w > stageW - 8) x = ax - w - GAP;      // no room right, go left
  if (x < 8) x = Math.min(Math.max(8, ax + aw / 2 - w / 2), stageW - w - 8);  // no room either side

  let y = ay + ah / 2 - h / 2;
  y = Math.min(Math.max(8, y), Math.max(8, stageH - h - 8));

  wrap.style.left = `${Math.round(x)}px`;
  wrap.style.top = `${Math.round(y)}px`;
  node = wrap;
}

/**
 * Make one card inspectable.
 *
 * ⚠️ Every listener that hides is registered, not just `pointerleave`: a card can be DESTROYED under
 * the pointer (it dies, it is played, the board re-renders), and a preview whose anchor no longer
 * exists would hang on screen with nothing to dismiss it.
 */
/** A deliberate hold on touch, longer than the mouse dwell: a finger rests on a screen by accident. */
const TOUCH_HOLD_MS = 420;
/** Past this, the finger is scrolling, not inspecting. */
const MOVE_TOLERANCE = 12;

export function attachZoom(el: HTMLElement, c: RuntimeObject): void {
  /*
   * ── MOUSE: RIGHT-CLICK. ───────────────────────────────────────────────────────────────────────
   *
   * 🚨 **ALEX, 2026-08-18:** *"i think for tooltips and enlarge it should not be scroll over, the
   * user should right click the mouse - that will make it less busy and annoying when you do not
   * want the tooltip or zoom in."*
   *
   * ⛔ **HOVER-TO-OPEN IS AN INVOLUNTARY TRIGGER.** A hand is seven cards wide and the board is
   * another eight, so simply moving the pointer to the END TURN button dragged a hold-timer across
   * half of them; the zoom fired on cards the player never intended to look at. A hold delay only
   * changes how often that happens, not whether it is the player's decision.
   *
   * ⭐ Right-click makes inspecting DELIBERATE, and it costs nothing: the left click is already the
   * play/select gesture, so the two never compete.
   *
   * ⚠️ **`preventDefault` ON CARDS ONLY, NEVER GLOBALLY.** Suppressing the browser's context menu
   * across the whole page would take away copy, inspect and back from every other surface.
   *
   * ⚠️ **AND IT STOPS PROPAGATION**, so the generic tooltip layer (`ui/tip.ts`, also on right-click)
   * does not open a text tip on top of the full card face. One gesture, one answer.
   */
  el.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (timer !== null) { window.clearTimeout(timer); timer = null; }
    show(el, c);
  });

  // Moving off the card, or starting a drag, closes it - the same dismissal hover had.
  el.addEventListener("pointerleave", hide);
  el.addEventListener("dragstart", hide);

  /*
   * ── TOUCH: LONG-PRESS. ────────────────────────────────────────────────────────────────────────
   *
   * 🔴 THIS WAS THE "real long-press affordance" THE OLD COMMENT SAID DID NOT EXIST YET, and while
   * it did not, **a card could not be inspected on a phone at all.** Not "was awkward" — there was
   * no path: touch does not fire `pointerenter` in a way a hover timer can use, and the only other
   * handler, `pointerdown`, HID the preview. Alex, on the deck builder: *"Click a card once, or
   * hover long enough for the tooltip, and it enlarges so the player can read it and see the art.
   * Required on the deck builder, where a card currently cannot be seen in full at all."*
   *
   * ⚠️ **THE PRESS MUST NOT ALSO ACT ON THE CARD.** A tap on a deck-builder card adds a copy, so a
   * long-press that opened the preview AND added a copy would be worse than no preview. Once the
   * hold fires, `suppressClick` is armed and the next `click` is eaten in the CAPTURE phase, before
   * the card's own handler runs.
   *
   * ⚠️ **AND A DRAG IS NOT A PRESS.** Moving more than `MOVE_TOLERANCE` cancels it, so scrolling a
   * long card list does not fill the screen with previews.
   */
  let startX = 0, startY = 0, held = false;

  el.addEventListener("pointerdown", (e) => {
    const pe = e as PointerEvent;
    if (pe.pointerType !== "touch") { hide(); return; }  // mouse: a press means they decided
    startX = pe.clientX; startY = pe.clientY; held = false;
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => { held = true; show(el, c); }, TOUCH_HOLD_MS);
  });

  el.addEventListener("pointermove", (e) => {
    const pe = e as PointerEvent;
    if (pe.pointerType !== "touch" || timer === null) return;
    if (Math.hypot(pe.clientX - startX, pe.clientY - startY) > MOVE_TOLERANCE) {
      window.clearTimeout(timer);
      timer = null;
    }
  });

  const endTouch = (e: Event) => {
    if ((e as PointerEvent).pointerType !== "touch") return;
    if (timer !== null) { window.clearTimeout(timer); timer = null; }
    if (held) {
      // Eat exactly one click, in CAPTURE, so the card's own tap handler never sees it.
      const eat = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
      el.addEventListener("click", eat, { capture: true, once: true });
      // If no click follows (the finger left the element), do not leave the trap armed.
      window.setTimeout(() => el.removeEventListener("click", eat, { capture: true }), 400);
      held = false;
    }
  };
  el.addEventListener("pointerup", endTouch);
  el.addEventListener("pointercancel", endTouch);
}

// ⚠️ Global escapes, because the anchor's own events cannot fire once it is gone.
window.addEventListener("keydown", (e) => { if (e.key === "Escape") hide(); });
window.addEventListener("scroll", hide, true);
window.addEventListener("blur", hide);
